import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import TopBar from '../../components/layout/TopBar';
import StickyFooter from '../../components/layout/StickyFooter';
import {
  Button, PhoneField, Skeleton, TextField,
} from '../../components/common';
import profileApi from '../../api/profile/profileApi';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { GENDERS } from '../../utils/constants';
import { maskIC } from '../../utils/format';
import * as v from '../../utils/validators';

export default function EditProfilePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { updateUser } = useAuth();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (val) => setForm((f) => ({ ...f, [k]: val }));

  useEffect(() => {
    profileApi.get().then((p) => setForm({
      name: p.name || '',
      preferredName: p.preferredName || '',
      countryCode: p.countryCode,
      phone: p.phone,
      email: p.email || '',
      icNumber: p.icNumber || '',
      dateOfBirth: p.dateOfBirth || '',
      gender: p.gender || '',
    }));
  }, []);

  const save = async () => {
    const errs = v.validate(form, {
      name: (x) => v.required(x, 'Full name'),
      phone: v.phone,
      email: v.email,
    });
    setErrors(errs);
    setFormError('');
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const { icNumber, ...payload } = form;
      const next = await profileApi.update(payload);
      updateUser(next);
      toast.show('Profile updated');
      navigate(-1);
    } catch (e) {
      setFormError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="page page--white page--with-footer">
      <TopBar title="Personal details" bordered />
      <div className="page__body">
        {!form ? <Skeleton count={5} height={72} /> : (
          <form onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
            {formError && <div className="alert u-mb-4"><IconInfoCircle size={18} />{formError}</div>}
            <TextField label="Full name (as per IC)" value={form.name} onChange={(e) => set('name')(e.target.value)} error={errors.name} />
            <TextField label="Preferred name" value={form.preferredName} onChange={(e) => set('preferredName')(e.target.value)} hint="Used in greetings and reminders." />
            <TextField
              label="IC number"
              value={maskIC(form.icNumber)}
              disabled
              hint="Contact the clinic to change your IC number."
            />
            <PhoneField
              countryCode={form.countryCode}
              onCountryCodeChange={set('countryCode')}
              value={form.phone}
              onChange={set('phone')}
              error={errors.phone}
            />
            <TextField label="Email" type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} error={errors.email} />
            <TextField label="Date of birth" type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth')(e.target.value)} />
            <div className="field">
              <div className="field__head"><span className="field__label">Gender</span></div>
              <div className="input">
                <select className="input__control select" value={form.gender} onChange={(e) => set('gender')(e.target.value)} aria-label="Gender">
                  <option value="">Prefer not to say</option>
                  {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            </div>
          </form>
        )}
      </div>
      <StickyFooter>
        <Button block onClick={save} loading={saving} disabled={!form}>Save changes</Button>
      </StickyFooter>
    </div>
  );
}
